// Upstream impact traversal (ARCHITECTURE.md §8.2, §8.3).
//
// "What does this depend on?" — follow forward dependency edges from the given
// files. The actual BFS lives in the shared `traverse` core; this module just
// fixes the direction to "upstream".

import type { EdgeClassification } from "../types.js";
import type { DependencyGraph } from "../dependency/types.js";
import type { EvidenceType } from "../evidence/types.js";
import type { AffectedEntry } from "./types.js";
import { traverse } from "./traverse.js";

export interface TraverseUpstreamOptions {
  graph: DependencyGraph;
  /** The files whose dependencies to walk. */
  files: string[];
  /** Maximum BFS depth to explore; default: unlimited. */
  maxDepth?: number;
  /** Edge classifications to traverse; default: deterministic + candidate. */
  edgeTypes?: EdgeClassification[];
  /** Evidence types to exclude when scoring entry relevance. */
  evidenceExclude?: EvidenceType[];
  /** Cap the number of returned entries. */
  limit?: number;
  /** @deprecated Issue #21 — use `edgeTypes`. Honoured for backward compat. */
  minConfidence?: number;
}

/**
 * Compute the files that `files` depend on by following forward dependency
 * edges. Returns AffectedEntry[] sorted by depth ascending.
 */
export function traverseUpstream(
  options: TraverseUpstreamOptions,
): AffectedEntry[] {
  const { graph, files, maxDepth, edgeTypes, evidenceExclude, limit, minConfidence } =
    options;
  return traverse({
    graph,
    seeds: files,
    direction: "upstream",
    maxDepth,
    ...(edgeTypes ? { edgeTypes } : {}),
    ...(evidenceExclude ? { evidenceExclude } : {}),
    ...(limit !== undefined ? { limit } : {}),
    ...(minConfidence !== undefined ? { minConfidence } : {}),
  });
}
